import { ReactElement, useEffect, useRef, useState } from 'react';
import { Button, HStack, Text, Textarea, VStack } from '@chakra-ui/react';
import {
  SubmitFeedbackSummaryPayload,
  WorksheetFeedbackNegativeSummaryTag,
  WorksheetFeedbackPositiveSummaryTag,
  WorksheetFeedbackSummaryRating,
  WorksheetFeedbackSummaryTag,
} from 'hooks/api/backend/worksheets/useWorksheets';
import { FeedbackSuccessMessage } from './FeedbackSuccessMessage';

const MAX_COMMENT_LENGTH = 500;

const DEFAULT_SHORT_MESSAGES: WorksheetFeedbackSummaryTag[] = [];

const NEGATIVE_TAG_OPTIONS: { id: WorksheetFeedbackSummaryTag; label: string }[] = [
  { id: WorksheetFeedbackNegativeSummaryTag.SHORT, label: 'Zu kurz' },
  { id: WorksheetFeedbackNegativeSummaryTag.INCORRECT, label: 'Falsch' },
  { id: WorksheetFeedbackNegativeSummaryTag.BLURRY, label: 'Unklar' },
  { id: WorksheetFeedbackNegativeSummaryTag.SUPERFICIAL, label: 'Oberflächlich' },
  { id: WorksheetFeedbackNegativeSummaryTag.CONFUSING, label: 'Verwirrend' },
  { id: WorksheetFeedbackNegativeSummaryTag.OVERLOADED, label: 'Zu viel auf einmal' },
];

const POSITIVE_TAG_OPTIONS: { id: WorksheetFeedbackSummaryTag; label: string }[] = [
  { id: WorksheetFeedbackPositiveSummaryTag.DETAILED, label: 'Ausführlich' },
  { id: WorksheetFeedbackPositiveSummaryTag.WELL_STRUCTURED, label: 'Gut strukturiert' },
  { id: WorksheetFeedbackPositiveSummaryTag.TIME_SAVING, label: 'Zeitsparend' },
  { id: WorksheetFeedbackPositiveSummaryTag.UNDERSTANDABLE, label: 'Verständlich' },
  { id: WorksheetFeedbackPositiveSummaryTag.HELPFUL, label: 'Hilfreich' },
  { id: WorksheetFeedbackPositiveSummaryTag.CONCISE, label: 'Prägnant' },
];

type ThumbRatingButtonProps = {
  emoji: string;
  ariaLabel: string;
  isSelected: boolean;
  onClick: () => void;
  disabled?: boolean;
};

const ThumbRatingButton = ({
  emoji,
  ariaLabel,
  isSelected,
  onClick,
  disabled = false,
}: ThumbRatingButtonProps): ReactElement => (
  <Button
    type="button"
    variant="secondaryOutline"
    size="m"
    aria-label={ariaLabel}
    aria-pressed={isSelected}
    onClick={onClick}
    disabled={disabled}
    px="m"
    py="s"
    minW="auto"
    bg={isSelected ? 'background.subtle.green' : 'background.primary'}
    borderColor={isSelected ? 'border.accent.active' : 'border.accent'}
    borderRadius="l"
  >
    {emoji}
  </Button>
);

type FeedbackTagChipProps = {
  label: string;
  isSelected: boolean;
  onClick: () => void;
  disabled?: boolean;
};

const FeedbackTagChip = ({
  label,
  isSelected,
  onClick,
  disabled = false,
}: FeedbackTagChipProps): ReactElement => (
  // Todo: Add as Design System component
  <Button
    variant="secondaryOutline"
    size="m"
    onClick={onClick}
    disabled={disabled}
    aria-pressed={isSelected}
    px="xs"
    py="3xs"
    minW="auto"
    h="auto"
    borderRadius="s"
    borderColor={isSelected ? 'border.accent.active' : 'border.accent'}
    bg={isSelected ? 'background.subtle.green' : 'background.primary'}
    flexShrink={0}
  >
    <Text textStyle="body.s.regular" color="text.primary">
      {label}
    </Text>
  </Button>
);

type Props = {
  summaryId: string;
  onSubmit: (payload: SubmitFeedbackSummaryPayload) => void | Promise<void>;
  isSubmitting?: boolean;
  disabled?: boolean;
};

export const WorksheetFeedbackSummaryReview = ({
  summaryId,
  onSubmit,
  isSubmitting = false,
  disabled = false,
}: Props): ReactElement | null => {
  // Show success message once feedback is submitted; hide form

  const [rating, setRating] = useState<WorksheetFeedbackSummaryRating | null>(null);
  const [selectedShortMessages, setSelectedShortMessages] =
    useState<WorksheetFeedbackSummaryTag[]>(DEFAULT_SHORT_MESSAGES);
  const [message, setMessage] = useState('');
  const [hasJustSubmitted, setHasJustSubmitted] = useState(false);
  const wasSubmittingRef = useRef(false);

  // Detect when submission completes and show success message
  useEffect(() => {
    if (wasSubmittingRef.current && !isSubmitting) {
      setHasJustSubmitted(true);
    }
    wasSubmittingRef.current = isSubmitting;
  }, [isSubmitting]);

  const showFeedbackDetails = rating !== null;
  const tagOptions =
    rating === WorksheetFeedbackSummaryRating.HELPFUL ? POSITIVE_TAG_OPTIONS : NEGATIVE_TAG_OPTIONS;

  const toggleShortMessage = (tag: WorksheetFeedbackSummaryTag): void => {
    if (disabled) return;
    setSelectedShortMessages((current) =>
      current.includes(tag) ? current.filter((item) => item !== tag) : [...current, tag]
    );
  };

  const handleSubmit = (): void => {
    if (disabled || rating === null) return;

    onSubmit({
      summaryId,
      rating,
      shortMessages: selectedShortMessages,
      message: message.trim() || undefined,
    });
  };

  if (hasJustSubmitted) {
    return <FeedbackSuccessMessage />;
  }

  return (
    <VStack align="stretch" gap="s" width="100%" aria-busy={disabled}>
      <HStack gap="s" align="center" flexWrap="wrap">
        <Text textStyle="body.m.medium" color="text.primary">
          War diese Zusammenfassung hilfreich?
        </Text>
        <HStack gap="xs">
          <ThumbRatingButton
            emoji="👍"
            ariaLabel="Hilfreich"
            isSelected={rating === WorksheetFeedbackSummaryRating.HELPFUL}
            onClick={() => setRating(WorksheetFeedbackSummaryRating.HELPFUL)}
            disabled={disabled}
          />
          <ThumbRatingButton
            emoji="👎"
            ariaLabel="Nicht hilfreich"
            isSelected={rating === WorksheetFeedbackSummaryRating.NOT_HELPFUL}
            onClick={() => setRating(WorksheetFeedbackSummaryRating.NOT_HELPFUL)}
            disabled={disabled}
          />
        </HStack>
      </HStack>

      {showFeedbackDetails && (
        <VStack align="stretch" gap="xs" width="100%">
          <Text textStyle="body.xs.regular" color="text.tertiary">
            Möchtest du uns mehr dazu sagen? (optional)
          </Text>
          <HStack gap="xs" flexWrap="wrap" align="flex-start">
            {tagOptions.map(({ id, label }) => (
              <FeedbackTagChip
                key={id}
                label={label}
                isSelected={selectedShortMessages.includes(id)}
                onClick={() => toggleShortMessage(id)}
                disabled={disabled}
              />
            ))}
          </HStack>
          <Textarea
            placeholder="z. B. „Die Zusammenfassung war zu vage“"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            maxLength={MAX_COMMENT_LENGTH}
            rows={3}
            minH="72px"
            bg="background.secondary"
            borderColor="border.accent.default"
            borderRadius="l"
            p="s"
            textStyle="body.s.regular"
            resize="vertical"
            disabled={disabled}
          />
          <Button
            type="button"
            variant="primary"
            size="m"
            alignSelf="flex-start"
            onClick={handleSubmit}
            loading={isSubmitting}
            disabled={disabled || rating === null}
            borderRadius="l"
          >
            Senden
          </Button>
        </VStack>
      )}
    </VStack>
  );
};
